import React from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Check, ChevronRight, ChevronLeft, Info, Cloud, Server } from 'lucide-react';
import SectionGuide from '../../components/SectionGuide';
import { ProgressIndicator } from './ProgressIndicator';
import { COMPLEXITY_LEVELS } from './constants';

export function WizardStep3({
  step, animKey, stepAnimClass, guideResetKey,
  complexity, setComplexity,
  hostingModel, setHostingModel,
  goToStep, setShowHostingInfo, setShowComplexityLevelInfo
}) {
  return (
    <div key={`step3-${animKey}`} className={stepAnimClass} data-testid="wizard-step-3">
      <SectionGuide id="estimator-complexity" title="Complexity & Hosting" accent="#F59E0B" resetKey={guideResetKey}>
        <p>
          <strong>Complexity</strong> sets the baseline effort hours for every component. A <em>Simple</em> deployment (single business unit, minimal integrations) needs far fewer hours than a <em>Complex</em> one (multi-region, custom workflows, heavy data migration). The <strong>hosting model</strong> affects infrastructure effort — private/on-premise deployments require more setup.
        </p>
        <p>
          These two choices together define the starting point for your estimate. You can still fine-tune individual component hours in the next step.
        </p>
      </SectionGuide>
      <ProgressIndicator step={step} goToStep={goToStep} />
      
      <div className="text-center mb-14">
        <h2 className="text-3xl md:text-4xl font-bold text-[#0F2040] font-['Outfit'] tracking-tight mb-4">
          How complex is your implementation?
        </h2>
        <p className="text-lg text-slate-500 max-w-xl mx-auto">
          This affects baseline hours and component recommendations
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto mb-12">
        {COMPLEXITY_LEVELS.map((level) => (
          <Card 
            key={level.id}
            className={`cursor-pointer transition-all duration-300 hover:shadow-lg hover:-translate-y-1 border ${
              complexity === level.id 
                ? `${level.borderColor} ${level.bgColor} ring-2 ring-[#0F2040] border-2` 
                : 'border-slate-200 hover:border-slate-300'
            }`}
            onClick={() => setComplexity(level.id)}
            data-testid={`complexity-${level.id}`}
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className={`w-12 h-12 rounded-xl bg-${level.color}-100 flex items-center justify-center`}>
                  <level.icon className={`w-6 h-6 text-${level.color}-600`} />
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-xs">
                    {level.timeEstimate}
                  </Badge>
                  <button
                    onClick={(e) => { e.stopPropagation(); setShowComplexityLevelInfo(level); }}
                    className="w-7 h-7 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors"
                    data-testid={`complexity-info-${level.id}`}
                  >
                    <Info className="w-3.5 h-3.5 text-slate-400" />
                  </button>
                </div>
              </div>
              <h3 className="text-xl font-semibold text-[#0F2040] mb-2">{level.title}</h3>
              <p className="text-slate-600 text-sm mb-4">{level.desc}</p>
              
              <div className="space-y-2">
                {level.examples.map((ex) => (
                  <div key={ex} className="flex items-center gap-2 text-xs text-slate-500">
                    <div className="w-1 h-1 rounded-full bg-slate-400" />
                    {ex}
                  </div>
                ))}
              </div>
              
              {complexity === level.id && (
                <div className="mt-4 pt-4 border-t border-slate-200 flex items-center gap-2 text-[#0F2040]">
                  <Check className="w-5 h-5" />
                  <span className="font-medium">Selected</span>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Hosting Model */}
      <div className="max-w-2xl mx-auto mb-12" data-testid="hosting-model-section">
        <h3 className="text-lg font-semibold text-[#0F2040] text-center mb-2">Hosting Model</h3>
        <p className="text-sm text-slate-500 text-center mb-5">Select the infrastructure deployment model</p>
        <div className="grid grid-cols-2 gap-4">
          <button
            onClick={() => setHostingModel('public_cloud')}
            className={`relative p-5 rounded-xl border-2 transition-all text-left ${
              hostingModel === 'public_cloud'
                ? 'border-[#0F2040] bg-[#0F2040]/5 ring-1 ring-[#0F2040]'
                : 'border-slate-200 hover:border-slate-300'
            }`}
            data-testid="hosting-public"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                hostingModel === 'public_cloud' ? 'bg-[#0F2040] text-white' : 'bg-slate-100 text-slate-500'
              }`}>
                <Cloud className="w-5 h-5" />
              </div>
              <div>
                <div className="font-semibold text-[#0F2040]">Public Cloud</div>
                <div className="text-xs text-slate-400">Shared hosting instance</div>
              </div>
            </div>
            <div className="flex items-center gap-3 mt-3 text-xs text-slate-500">
              <span className="bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full font-medium">2 hrs setup</span>
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => { e.stopPropagation(); setShowHostingInfo(true); }}
                className="flex items-center gap-1 bg-slate-100 px-2 py-0.5 rounded-full hover:bg-slate-200 transition-colors cursor-pointer"
                data-testid="hosting-modifier-info-public"
              >
                No modifier <Info className="w-3 h-3 text-slate-400" />
              </span>
            </div>
            {hostingModel === 'public_cloud' && (
              <div className="absolute top-3 right-3"><Check className="w-5 h-5 text-[#0F2040]" /></div>
            )}
          </button>

          <button
            onClick={() => setHostingModel('private_cloud')}
            className={`relative p-5 rounded-xl border-2 transition-all text-left ${
              hostingModel === 'private_cloud'
                ? 'border-[#0F2040] bg-[#0F2040]/5 ring-1 ring-[#0F2040]'
                : 'border-slate-200 hover:border-slate-300'
            }`}
            data-testid="hosting-private"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                hostingModel === 'private_cloud' ? 'bg-[#0F2040] text-white' : 'bg-slate-100 text-slate-500'
              }`}>
                <Server className="w-5 h-5" />
              </div>
              <div>
                <div className="font-semibold text-[#0F2040]">Private Cloud</div>
                <div className="text-xs text-slate-400">Dedicated hosting instance</div>
              </div>
            </div>
            <div className="flex items-center gap-3 mt-3 text-xs text-slate-500">
              <span className="bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full font-medium">32 hrs setup</span>
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => { e.stopPropagation(); setShowHostingInfo(true); }}
                className="flex items-center gap-1 bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full hover:bg-amber-100 transition-colors cursor-pointer"
                data-testid="hosting-modifier-info-private"
              >
                +5% modifier <Info className="w-3 h-3" />
              </span>
            </div>
            {hostingModel === 'private_cloud' && (
              <div className="absolute top-3 right-3"><Check className="w-5 h-5 text-[#0F2040]" /></div>
            )}
          </button>
        </div>
      </div>

      <div className="flex justify-between max-w-5xl mx-auto">
        <Button variant="outline" onClick={() => goToStep(2)} data-testid="step3-back-btn">
          <ChevronLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <Button 
          onClick={() => goToStep(4)}
          disabled={!complexity}
          className="bg-[#4ABFCF] hover:bg-[#3AAAB9] text-white"
          data-testid="step3-continue-btn"
        >
          Configure Components
          <ChevronRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}
